import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { InstrumentDetailsPageBase } from '@zero/instrument-details/feature-instrument-details/base';
import { LeverageProductsComponent } from '@zero/leverage-products/feature-leverage-products/web';
import { TradingViewChartWebComponent } from '@zero/shared/charts/ui-charts-web';
import { EqualsInstrumentTypePipe } from '@zero/shared/ui-pipes';
import { BackLinkComponent } from '@zero/shared/ui-web-components';
import { AuthService } from '@zero/shared/util-web-app-services';
import { TradeComponent } from '@zero/trade/feature-trade/web';
import { commonRoutes } from 'constants/web-app-constants';
import { filter, map } from 'rxjs';
import { BaseDataWebComponent } from '../../components/base-data-web/base-data-web.component';
import { DerivativeBaseAndKeyDataComponent } from '../../components/derivative-base-and-key-data/derivative-base-and-key-data.component';
import { DerivativeProductDescriptionComponent } from '../../components/derivative-product-description/derivative-product-description.component';
import { DividendsWebComponent } from '../../components/dividends/dividends-web.component';
import { DocumentsWebComponent } from '../../components/documents-web/documents-web.component';
import { HeaderWebComponent } from '../../components/header-web/header-web.component';
import { KeyDataWebComponent } from '../../components/key-data-web/key-data-web.component';
import { KnockedOutHintComponent } from '../../components/knocked-out-hint/knocked-out-hint.component';
import { NewsWebComponent } from '../../components/news-web/news-web.component';
import { PositionWebComponent } from '../../components/position-web/position-web.component';
import { ProfileWebComponent } from '../../components/profile-web/profile-web.component';
import { RelatedLinksComponent } from '../../components/related-links/related-links.component';
import { StatisticsWebComponent } from '../../components/statistics-web/statistics-web.component';
import { TopflopListWebComponent } from '../../components/topflop-list-web/topflop-list-web.component';

@Component({
  selector: 'instrument-details-instrument-details-page-web',
  templateUrl: './instrument-details-page-web.component.html',
  imports: [
    HeaderWebComponent,
    TradingViewChartWebComponent,
    PositionWebComponent,
    StatisticsWebComponent,
    KeyDataWebComponent,
    BaseDataWebComponent,
    DocumentsWebComponent,
    LeverageProductsComponent,
    NewsWebComponent,
    DividendsWebComponent,
    ProfileWebComponent,
    TopflopListWebComponent,
    RouterLink,
    AsyncPipe,
    EqualsInstrumentTypePipe,
    TranslocoPipe,
    TradeComponent,
    RelatedLinksComponent,
    DerivativeBaseAndKeyDataComponent,
    DerivativeProductDescriptionComponent,
    KnockedOutHintComponent,
    NgTemplateOutlet,
    BackLinkComponent,
  ],
})
export class InstrumentDetailsPageWebComponent extends InstrumentDetailsPageBase implements OnInit, OnDestroy {
  public override homeUrl = commonRoutes.postlogin.member_home;
  readonly #authService = inject(AuthService);
  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly #destroyRef = inject(DestroyRef);

  public readonly isPotentialCustomer$ = this.#authService.isPotentialCustomer();

  public readonly isMobileViewport$ = this.#breakpointObserver.observe(['(max-width: 991px)']).pipe(
    map((state: BreakpointState) => Boolean(state.matches)),
    takeUntilDestroyed(this.#destroyRef),
  );

  public readonly displayOrdermask$ = this.activatedRoute.queryParams.pipe(
    map((q) => q['ordermask']),
    filter((ordermask) => ordermask === 'true'),
  );

  public ngOnInit(): void {
    this.#listenToLoginState();
  }

  public ngOnDestroy(): void {
    super.destroy();
  }

  #listenToLoginState() {
    this.#authService
      .isLoggedIn$()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((isLoggedIn) => {
        isLoggedIn ? super.init() : this.router.navigate([this.homeUrl]);
      });
  }
}
